import * as Clipboard from "expo-clipboard";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { ViewType } from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { extractJewelryWithStoneDetailsOrder } from "@scenes/configurator/OrderCart/utils";
import { PdpSections } from "@utils/customization";
import { isPair, isParcel } from "@utils/product/productValidators";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";

const getRequestType = (request_type: string) => {
  if (request_type) {
    if (request_type.includes("buy")) {
      return RequestTypes.Buy;
    } else if (request_type.includes("hold")) {
      return RequestTypes.Hold;
    } else if (request_type.includes("memo")) {
      return RequestTypes.Memo;
    } else if (request_type.includes("ask")) {
      return RequestTypes.Questions;
    } else if (request_type.includes("rbr")) {
      return RequestTypes.RBR;
    } else if (request_type.includes("order")) {
      return RequestTypes.Order;
    } else if (request_type.includes("quote")) {
      return RequestTypes.Quote;
    }
  }
  return RequestTypes.Buy;
};

const enum RequestTypes {
  Buy = "Buy",
  Memo = "Memo",
  Hold = "Hold",
  Questions = "Questions",
  RBR = "RBR",
  Internal = "Internal",
  Order = "order",
  Quote = "Quote",
  diamondMemo = "diamond_memo",
}

const isListedPriceEqualsOfferPrice = (request: any, productType: any) => {
  const offerTsp = (request?.offer_total_sales_price * 100) / 100;
  const listedTsp = (request[productType]?.total_sales_price * 100) / 100;
  return (
    Number(formatNumberWithPrecision(listedTsp, { precision: 0 })) ===
    Number(formatNumberWithPrecision(offerTsp, { precision: 0 }))
  );
};

const getFirstNonNullNote = (request) => {
  if (request?.children && Array.isArray(request.children)) {
    const childWithNotes = request.children.find(
      (child) => child.notes !== null && child.notes !== undefined
    );
    return childWithNotes?.notes || null;
  }
  return null;
};

const isAnyChildWithdrawn = (request) => {
  if (request?.children && Array.isArray(request.children)) {
    return request.children.some((child) => child.status === "withdraw");
  }
  return false;
};

const requestType = ({ stockItemRequest, productType }: any) => {
  const bmhRequestType = stockItemRequest.bmh_request_type;
  const requestTypeStr = stockItemRequest.request_type;
  if (stockItemRequest.bmh_request_type === "retail_buy_request") {
    return 4;
  }
  if (bmhRequestType === "retail_buy_request") {
    return 4;
  } else if (bmhRequestType === "purchasing_agent") {
    return 5;
  }
  switch (requestTypeStr) {
    case `buy_${productType}`:
      return 1;
    case `${productType}_memo`:
      return 2;
    case `hold_${productType}`:
      return 3;
    case `bulk_buy_${productType}`:
      return 6;
    case `bulk_${productType}_memo`:
      return 7;
    case `bulk_hold_${productType}`:
      return 8;
    case `bulk_quote_${productType}`:
      return 9;
    default:
      return 1;
  }
};

interface RequestTypeMap {
  name: string;
  theme: string;
}

const requestTypeMap = (
  t: (key: string, fallback: string) => string
): Record<string, RequestTypeMap> => ({
  diamond: {
    name: t("global.text.naturalDiamonds", "Natural Diamonds"),
    theme: "diam-01",
  },
  gemstone: {
    name: t("global.text.coloredGemstones", "Colored Gemstones"),
    theme: "gem-01",
  },
  jewelry: { name: t("global.text.jewelry", "Jewelry"), theme: "jewelry-01" },
  lab_grown_diamond: {
    name: t("global.text.labGrownDiamonds", "Lab-Grown Diamonds"),
    theme: "lab-01",
  },
  default: {
    name: t("global.text.multipleOrder", "Multiple Order"),
    theme: "main-03",
  },
});

const copyStock = async (
  stockItemRequest,
  hasChildren,
  getStockNumber,
  t,
  enableJewelryBuilder,
  orderCartRequest,
  selectedItems?: any[]
) => {
  let stockNumbers;
  if (orderCartRequest) {
    stockNumbers = stockItemRequest.ordered_items.flatMap((child) => {
      if (child.group_type === PdpSections.JEWELRY_BUILDER) {
        return child.items.map((item) =>
          getStockNumber(item.item, orderCartRequest)
        );
      }

      return getStockNumber(child.item, orderCartRequest);
    });
  } else if (
    hasChildren ||
    (enableJewelryBuilder &&
      stockItemRequest.secondary_request_type === PdpSections.JEWELRY_BUILDER)
  ) {
    const children = stockItemRequest?.children ?? [];
    const filteredChildren =
      selectedItems && selectedItems.length > 0
        ? children.filter((child) => selectedItems.includes(child?.id))
        : children;
    stockNumbers = filteredChildren.map(getStockNumber);
  } else {
    stockNumbers = [getStockNumber(stockItemRequest)];
  }
  await Clipboard.setStringAsync(stockNumbers.join(", "));
  showToast(
    t("requestHistoryDetail.success.text", "Success"),
    t("requestHistoryDetail.stockItem.toastMessage.text"),
    ToastTypes.SUCCESS,
    true,
    true
  );
};
const isValidValue = (value: string) => {
  const num = parseFloat(value);
  return num > 0;
};
const showRequestItemFromSupplier = (item) => {
  if (item !== null && item?.view_type === ViewType.SellerView) {
    if (
      item?.can_request_to_supplier &&
      item.deep_link &&
      item.deep_link.trim() !== ""
    ) {
      return true;
    }

    if (
      item?.bmh_request_type === "purchasing_agent" ||
      item?.bmh_request_type === "retail_buy_request"
    ) {
      if (
        item.original_seller_location &&
        item.original_seller_location?.id &&
        item.buyer_location &&
        item.buyer_location?.id
      ) {
        return item.original_seller_location?.id !== item.buyer_location?.id;
      }
    }
  }
  return false;
};

function compareFormValues(current: any, initial: any) {
  return JSON.stringify(current) !== JSON.stringify(initial);
}
const getOrderStockType = (order?: any) => {
  const processedArray = extractJewelryWithStoneDetailsOrder(
    order?.ordered_items
  );
  const children = normalizeItems(processedArray);
  const stockTypes = children?.map(
    (item) => item?.stock_type || item?.item?.stock_type
  );

  const uniqueStockTypes = Array.from(new Set(stockTypes));

  if (uniqueStockTypes.length === 1) {
    return uniqueStockTypes[0];
  } else {
    return ProductCategory.DEFAULT;
  }
};

const CLOSED_QUOTE_LINE_STATUSES = new Set([
  "withdraw",
  "withdrawn",
  "accepted",
  "decline",
  "declined",
  "expired",
  "cancelled",
  "canceled",
  "closed",
]);

const resolveLineStatus = (line: any) => line?.status;

const isPendingRequestItem = (item: any) =>
  String(item?.status ?? "")
    .trim()
    .toLowerCase() === "pending";

const isOpenQuoteLine = (item: any) => {
  const status = String(resolveLineStatus(item) ?? "")
    .trim()
    .toLowerCase();
  if (!status || status === "pending") return true;
  return !CLOSED_QUOTE_LINE_STATUSES.has(status);
};

function normalizeItems(arr) {
  return Array.isArray(arr)
    ? arr.map((element) => {
        const status = resolveLineStatus(element);
        if ("item" in element && "total_price_based_on_quantity" in element) {
          return {
            total_price_based_on_quantity:
              element.total_price_based_on_quantity,
            ...element.item,
            id: element.id ?? element.item?.id,
            ...(status != null ? { status } : {}),
          };
        }
        return {
          total_price_based_on_quantity: element.total_price_based_on_quantity,
          ...element,
          ...(status != null ? { status } : {}),
        };
      })
    : [];
}

const normalizeOrderResponse = (response, typeText) => {
  const order = response?.order;
  const processedArray = extractJewelryWithStoneDetailsOrder(
    order?.ordered_items
  );
  const children = normalizeItems(processedArray);
  return {
    ...order,
    children,
    id: order?.id ?? "",
    order_id: order?.order_id ?? "",
    buyer_location: {
      ...order?.buyer_details,
      company_name: order?.buyer_details?.location_name ?? "",
    },
    seller_location: {
      ...order?.seller,
      company_name: order?.seller?.location_name ?? "",
    },
    offer_total_sales_price: order?.payment_details?.total_amount ?? "",
    view_type: order?.view_type ?? "",
    request_type: RequestTypes.Order,
    type_text: typeText ?? "",
    status: order?.view_type === "seller_view" ? "received" : "submitted",
  };
};

const normalizeOrderTypeKey = (value: string) =>
  value.toLowerCase().replace(/[\s_]/g, "");

const recipientDetailKeys = new Set([
  "radioselect",
  "recipientname",
  "recipientemail",
  "recipientid",
  "recipientphonenumber",
  "phonenumber",
  "countrycode",
  "recipientcompanyname",
  "recipientcompanylocation",
  "companylocation",
  "companylocations",
  "comments",
  "accountid",
]);

const getOrderTypeDisplayRows = (
  attributes: Record<string, any> | undefined,
  _orderTypeForm: any,
  translate: (key: string, fallback?: string) => string
) => {
  if (!attributes || typeof attributes !== "object") return [];

  const rows = Object.entries(attributes)
    .map(([attrKey, attrValue]) => ({
      key: normalizeOrderTypeKey(attrKey),
      label: attrKey,
      value: String(attrValue ?? "").trim(),
    }))
    .filter(
      (row) =>
        !!row.value && row.value !== "-" && !recipientDetailKeys.has(row.key)
    );

  if (!rows.some((row) => row.key === "ordertype")) return [];

  return rows.map((row) => ({
    label:
      row.key === "ordertype"
        ? translate("requestHistoryDetail.orderType.text", "Order Type")
        : row.label,
    value: row.value,
  }));
};

const parseOrderComments = (commentString: any) => {
  if (typeof commentString !== "string") return null;

  const lines = commentString.split("\n").filter((line) => line.trim() !== "");
  if (lines.length === 0) return { comments: commentString };
  const firstLine = lines[0].trim();
  const result = {};

  lines.forEach((line) => {
    const match = line.match(/^\s*([^:]+):\s*"?([^"]*)"?$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      result[key] = value;
    }
  });

  if (Object.keys(result).length > 0 && firstLine) {
    return { attributes: result, comments: firstLine };
  } else if (Object.keys(result).length) {
    return { attributes: result };
  } else {
    return { comments: firstLine };
  }
};

const enrichStockItemRequest = (
  stockItemRequest: any,
  productType: string,
  t: (key: string, fallback: string) => string,
  isCustomApp?: boolean
) => {
  if (!stockItemRequest || !productType) return stockItemRequest;

  const productKey = productType;
  let updatedProductData = { ...stockItemRequest?.[productKey] };
  if (
    isPair(updatedProductData) ||
    (isCustomApp && isParcel(updatedProductData))
  ) {
    if (
      stockItemRequest?.offer_total_sales_price !== null &&
      stockItemRequest?.offer_price_per_carat !== null &&
      stockItemRequest?.offer_discount_percent !== null
    ) {
      updatedProductData = {
        ...updatedProductData,
        updated_total_sales_price: stockItemRequest.offer_total_sales_price,
        updated_price_per_carat: stockItemRequest.offer_price_per_carat,
        updated_discount_percent: stockItemRequest.offer_discount_percent,
      };
    }
  }

  return {
    ...stockItemRequest,
    [productKey]: updatedProductData,
    type_text: requestTypeMap(t)[productType]?.name || "",
  };
};

export {
  getRequestType,
  requestTypeMap,
  isListedPriceEqualsOfferPrice,
  requestType,
  RequestTypes,
  getFirstNonNullNote,
  isAnyChildWithdrawn,
  copyStock,
  showRequestItemFromSupplier,
  isValidValue,
  compareFormValues,
  getOrderStockType,
  normalizeOrderResponse,
  parseOrderComments,
  getOrderTypeDisplayRows,
  enrichStockItemRequest,
  isOpenQuoteLine,
  isPendingRequestItem,
};
